import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableService,
} from '@web/shared/ui';
import { Route, RouteParam } from '@web/common';
import { Observable, of } from 'rxjs';
import { WarehouseRestockListCustomCellTemplateComponent } from '../../components/warehouse-restock-list-custom-cell-template/warehouse-restock-list-custom-cell-template.component';
import { WarehouseRestockCollectionItem } from '../../models/warehouse-restock-collection-item';
import { WarehouseRestockCollectionFilter } from '../../models/warehouse-restock-collection-filter';
import { WarehouseRestockListItemAction } from '../../enums/warehouse-restock-list-item-action';
import { WarehouseRestockListAction } from '../../enums/warehouse-restock-list-action';
import { WarehouseRestockDataSourceService } from '../../services/warehouse-restock-data-source.service';
import { WarehouseRestockStatusProviderService } from '../../providers/warehouse-restock-status-provider.service';
import { WarehouseRestockTypeProviderService } from '../../providers/warehouse-restock-type-provider.service';
import { once } from '@web/utils';

@Component({
  selector: 'app-warehouse-restock-list',
  imports: [TableComponent, WarehouseRestockListCustomCellTemplateComponent],
  templateUrl: './warehouse-restock-list.component.html',
  styleUrl: './warehouse-restock-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseRestockListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<WarehouseRestockCollectionItem, WarehouseRestockCollectionFilter, WarehouseRestockListItemAction, never, WarehouseRestockListAction>).create();
  private readonly table = inject(TableService);
  /* prettier-ignore */
  private readonly warehouseRestockDataSource = inject(WarehouseRestockDataSourceService);
  /* prettier-ignore */
  private readonly restockStatusProvider = inject(WarehouseRestockStatusProviderService);
  /* prettier-ignore */
  private readonly restockTypeProvider = inject(WarehouseRestockTypeProviderService);

  public readonly createRestockVisibility = input<boolean>(false);

  protected readonly config = this.buildTable();

  private buildTable(): TableConfig<
    WarehouseRestockCollectionItem,
    WarehouseRestockCollectionFilter,
    WarehouseRestockListItemAction,
    never,
    WarehouseRestockListAction
  > {
    return this.tableBuilder
      .dataKey('id')
      .columns((builder) => [
        builder
          .initText('number')
          .filterKey('number')
          .sortKey('number')
          .filterable(true)
          .sortable(true)
          .label('Numer zwrotu')
          .build(),

        builder
          .initText('shippingNumber')
          .filterKey('shippingNumber')
          .sortKey('shippingNumber')
          .filterable(true)
          .sortable(true)
          .label('Numer zlecenia dostawy')
          .build(),

        builder
          .initCustom('orderNumber')
          .filterKey('orderNumber')
          .width('16rem')
          .sortKey('orderNumber')
          .filterable(true)
          .sortable(true)
          .label('Numer zamówienia')
          .build(),

        builder
          .initText('personDignity')
          .filterKey('personDignity')
          .sortKey('personDignity')
          .filterable(true)
          .sortable(true)
          .label('Imię i nazwisko')
          .build(),

        builder
          .initText('personEmail')
          .filterKey('personEmail')
          .sortKey('personEmail')
          .filterable(true)
          .sortable(true)
          .label('Adres e-mail')
          .build(),

        builder
          .initText('personPhone')
          .filterKey('personPhone')
          .sortKey('personPhone')
          .filterable(true)
          .sortable(true)
          .label('Numer telefonu')
          .build(),

        builder
          .initCustom('type')
          .width('10rem')
          .filterKey('type')
          .sortKey('type')
          .filterable(true)
          .sortable(true)
          .label('Rodzaj zwrotu')
          .build(),

        builder
          .initCustom('status')
          .width('10rem')
          .filterKey('status')
          .sortKey('status')
          .filterable(true)
          .sortable(true)
          .label('Status')
          .build(),

        builder
          .initDate('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .filterable(true)
          .sortable(true)
          .label('Data utworzenia')
          .build(),

        builder
          .initDate('restockedAt')
          .filterKey('restockedAt')
          .sortKey('restockedAt')
          .filterable(true)
          .sortable(true)
          .label('Data zwrotu')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(WarehouseRestockListAction.Create)
          .routerLink((provider) =>
            provider.routerLink(Route.WarehouseRestockCreate),
          )
          .label('Dodaj zwrot')
          .visibility(() => of(this.createRestockVisibility()))
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('number')
          .build(),

        builder //
          .initText('shippingNumber')
          .build(),

        builder //
          .initText('personDignity')
          .build(),

        builder //
          .initText('personEmail')
          .build(),

        builder //
          .initText('personPhone')
          .build(),

        builder //
          .initSelect('type')
          .dictionary(() => this.restockTypeProvider.selectItems())
          .build(),

        builder //
          .initSelect('status')
          .dictionary(() => this.restockStatusProvider.selectItems())
          .build(),

        builder //
          .initDateRange('restockedAt')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(WarehouseRestockListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseRestockDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .tooltip('Podgląd')
          .icon(Icon.Eye)
          .build(),

        builder
          .initRouterLink(WarehouseRestockListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseRestockEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .tooltip('Edytuj')
          .icon(Icon.Pencil)
          .build(),

        builder
          .initMore(WarehouseRestockListItemAction.More)
          .visibility((item) => item.canBeApproved || item.canBeRejected)
          .children((builder) => [
            builder
              .initCommand(WarehouseRestockListItemAction.Approve)
              .visibility((item) => item.canBeApproved)
              .command((item) => this.approve(item))
              .tooltip('Zatwierdź')
              .icon(Icon.CheckCircle)
              .build(),

            builder
              .initCommand(WarehouseRestockListItemAction.Reject)
              .visibility((item) => item.canBeRejected)
              .command((item) => this.reject(item))
              .tooltip('Odrzuć')
              .icon(Icon.XCircle)
              .build(),
          ])
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad,
        ): Observable<TableDataPayload<WarehouseRestockCollectionItem>> =>
          this.warehouseRestockDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private approve(item: WarehouseRestockCollectionItem): void {
    this.warehouseRestockDataSource
      .approveById(item.id)
      .pipe(once())
      .subscribe({
        next: () => this.refresh(),
      });
  }

  private reject(item: WarehouseRestockCollectionItem): void {
    this.warehouseRestockDataSource
      .rejectById(item.id)
      .pipe(once())
      .subscribe({
        next: () => this.refresh(),
      });
  }

  private refresh(): void {
    this.table.refresh();
  }
}
